// @intent: Gesture helpers over the runtime-owned design-space pointer stream
// @flow: PixiPlayableAPI creates helpers -> generated code uses onTap/onSwipe/onDrag without coordinate math
// @stale: Keep synchronized with PlayableInput additions in @mintyads/playable-api

import type {
  PlayableDragEvent,
  PlayableDragHandlers,
  PlayableDragOptions,
  PlayableInput,
  PlayablePoint,
  PlayablePointerEvent,
  PlayablePointerEventType,
  PlayablePointerHandler,
  PlayableSwipeDirection,
  PlayableSwipeEvent,
  PlayableSwipeOptions,
  PlayableTapEvent,
  PlayableTapOptions,
} from '@mintyads/playable-api';
import type { RuntimeInputPayload } from '@mintyads/shared-types';
import type { RuntimeCleanupScope } from './runtimeCleanup';

export interface RuntimeInputHelpersOptions {
  readonly onPointer: (type: PlayablePointerEventType, handler: PlayablePointerHandler) => () => void;
  readonly onInputEvidence?: ((evidence: RuntimeInputPayload) => void) | undefined;
  readonly cleanupScope?: RuntimeCleanupScope | undefined;
  readonly now?: (() => number) | undefined;
}

interface ActivePointer {
  readonly pointerId: number;
  readonly pointerType: string;
  readonly start: PlayablePoint;
  readonly startedAt: number;
  previous: PlayablePoint;
  lastEvent: PlayablePointerEvent;
}

function distance(left: PlayablePoint, right: PlayablePoint): number {
  return Math.hypot(right.x - left.x, right.y - left.y);
}

function matchesPointerType(event: PlayablePointerEvent, pointerType: string | undefined): boolean {
  return pointerType === undefined || event.pointerType === pointerType;
}

function samePointer(event: PlayablePointerEvent, active: ActivePointer | null): active is ActivePointer {
  return active !== null && event.pointerId === active.pointerId;
}

function dominantDirection(deltaX: number, deltaY: number): PlayableSwipeDirection {
  if (Math.abs(deltaX) >= Math.abs(deltaY)) return deltaX >= 0 ? 'right' : 'left';
  return deltaY >= 0 ? 'down' : 'up';
}

function once(cleanup: () => void): () => void {
  let called = false;
  return () => {
    if (called) return;
    called = true;
    cleanup();
  };
}

function combineCleanups(cleanups: readonly (() => void)[]): () => void {
  return once(() => {
    for (const cleanup of cleanups) cleanup();
  });
}

function trackCleanup(cleanupScope: RuntimeCleanupScope | undefined, cleanup: () => void): () => void {
  const tracked = once(cleanup);
  cleanupScope?.add(tracked);
  return tracked;
}

function activeFrom(event: PlayablePointerEvent, now: number): ActivePointer {
  const point = { x: event.x, y: event.y };
  return {
    pointerId: event.pointerId,
    pointerType: event.pointerType,
    start: point,
    previous: point,
    lastEvent: event,
    startedAt: now,
  };
}

function inputEvidenceFrom(
  event: PlayablePointerEvent,
  gesture: RuntimeInputPayload['gesture'],
  direction?: PlayableSwipeDirection,
): RuntimeInputPayload {
  return {
    gesture,
    phase: 'accepted',
    designX: event.x,
    designY: event.y,
    pointerId: event.pointerId,
    pointerType: event.pointerType,
    isPrimary: event.isPrimary,
    ...(direction === undefined ? {} : { direction }),
  };
}

function dragEventFrom(input: {
  readonly active: ActivePointer;
  readonly event: PlayablePointerEvent;
  readonly now: number;
}): PlayableDragEvent {
  const current = { x: input.event.x, y: input.event.y };
  return {
    ...input.event,
    start: input.active.start,
    previous: input.active.previous,
    current,
    deltaX: current.x - input.active.previous.x,
    deltaY: current.y - input.active.previous.y,
    totalDeltaX: current.x - input.active.start.x,
    totalDeltaY: current.y - input.active.start.y,
    durationMs: Math.max(0, input.now - input.active.startedAt),
  };
}

export function createRuntimeInputHelpers(options: RuntimeInputHelpersOptions): PlayableInput {
  const now = options.now ?? (() => Date.now());
  const emitInputEvidence = options.onInputEvidence ?? (() => {});
  const rawOn = (type: PlayablePointerEventType, handler: PlayablePointerHandler): (() => void) => {
    const cleanup = options.onPointer(type, (event) => {
      emitInputEvidence(inputEvidenceFrom(event, 'pointer'));
      handler(event);
    });
    return trackCleanup(options.cleanupScope, cleanup);
  };

  return {
    on: rawOn,
    onTap: (handler: (event: PlayableTapEvent) => void, tapOptions: PlayableTapOptions = {}) => {
      const maxDistance = tapOptions.maxDistance ?? 12;
      let active: ActivePointer | null = null;
      const cleanup = combineCleanups([
        rawOn('pointerdown', (event) => {
          if (!event.isPrimary || !matchesPointerType(event, tapOptions.pointerType)) return;
          active = activeFrom(event, now());
        }),
        rawOn('pointerup', (event) => {
          if (!samePointer(event, active)) return;
          const started = active;
          active = null;
          const end = { x: event.x, y: event.y };
          if (distance(started.start, end) > maxDistance) return;
          emitInputEvidence(inputEvidenceFrom(event, 'tap'));
          handler({
            ...event,
            start: started.start,
            durationMs: Math.max(0, now() - started.startedAt),
          });
        }),
        rawOn('pointercancel', (event) => {
          if (samePointer(event, active)) active = null;
        }),
      ]);
      return cleanup;
    },
    onSwipe: (handler: (event: PlayableSwipeEvent) => void, swipeOptions: PlayableSwipeOptions = {}) => {
      const minDistance = swipeOptions.minDistance ?? 24;
      let active: ActivePointer | null = null;
      const cleanup = combineCleanups([
        rawOn('pointerdown', (event) => {
          if (!event.isPrimary || !matchesPointerType(event, swipeOptions.pointerType)) return;
          active = activeFrom(event, now());
        }),
        rawOn('pointerup', (event) => {
          if (!samePointer(event, active)) return;
          const started = active;
          active = null;
          const end = { x: event.x, y: event.y };
          const deltaX = end.x - started.start.x;
          const deltaY = end.y - started.start.y;
          const dominantDistance = Math.max(Math.abs(deltaX), Math.abs(deltaY));
          if (dominantDistance < minDistance) return;
          const direction = dominantDirection(deltaX, deltaY);
          emitInputEvidence(inputEvidenceFrom(event, 'swipe', direction));
          handler({
            ...event,
            start: started.start,
            end,
            deltaX,
            deltaY,
            distance: Math.hypot(deltaX, deltaY),
            direction,
            durationMs: Math.max(0, now() - started.startedAt),
          });
        }),
        rawOn('pointercancel', (event) => {
          if (samePointer(event, active)) active = null;
        }),
      ]);
      return cleanup;
    },
    onDrag: (handlers: PlayableDragHandlers, dragOptions: PlayableDragOptions = {}) => {
      let active: ActivePointer | null = null;
      let started = false;
      const minDistance = dragOptions.minDistance ?? 0;
      const cleanup = combineCleanups([
        rawOn('pointerdown', (event) => {
          if (!event.isPrimary || !matchesPointerType(event, dragOptions.pointerType)) return;
          active = activeFrom(event, now());
          started = minDistance <= 0;
          if (started) {
            emitInputEvidence(inputEvidenceFrom(event, 'drag'));
            handlers.start?.(dragEventFrom({ active, event, now: now() }));
          }
        }),
        rawOn('pointermove', (event) => {
          if (!samePointer(event, active)) return;
          const activePointer = active;
          if (!started && distance(activePointer.start, { x: event.x, y: event.y }) >= minDistance) {
            started = true;
            emitInputEvidence(inputEvidenceFrom(event, 'drag'));
            handlers.start?.(dragEventFrom({ active: activePointer, event, now: now() }));
          }
          if (started) handlers.move?.(dragEventFrom({ active: activePointer, event, now: now() }));
          activePointer.previous = { x: event.x, y: event.y };
          activePointer.lastEvent = event;
        }),
        rawOn('pointerup', (event) => {
          if (!samePointer(event, active)) return;
          const activePointer = active;
          active = null;
          if (started) handlers.end?.(dragEventFrom({ active: activePointer, event, now: now() }));
          started = false;
        }),
        rawOn('pointercancel', (event) => {
          if (!samePointer(event, active)) return;
          const activePointer = active;
          active = null;
          if (started) handlers.cancel?.(dragEventFrom({ active: activePointer, event, now: now() }));
          started = false;
        }),
      ]);
      return cleanup;
    },
  };
}
